/**
 * Wire protocol for the `multipleChoice` interactive tool (ADR-091).
 *
 * The tool does no server work: `execute` echoes its validated input, the
 * client renders a picker in place of the composer, and the user's pick
 * arrives as their next chat message. Everything a client needs to detect
 * the prompt and answer it lives here; the tool definition itself
 * (model-facing schema, `execute`, `toModelOutput`) stays in
 * `@workspace/ai`, which re-exports these symbols so there is one source of
 * truth for the web picker, mobile, and the persist-step finalizer.
 */

import { z } from "zod";

/**
 * Display state of one `multipleChoice` call (its `input`, echoed as
 * `output`). This is the settled shape; while a call streams or rehydrates
 * its parts are deep-partials, so pickers read them through
 * {@link coerceMultipleChoiceState} rather than parsing against this schema.
 */
export const sdkMultipleChoiceStateSchema = z.object({
  allowOther: z.boolean(),
  multiple: z.boolean(),
  options: z.array(z.string()),
  question: z.string(),
});

export type SdkMultipleChoiceState = z.infer<
  typeof sdkMultipleChoiceStateSchema
>;

/**
 * Tool name as registered in the `base` capability. Shared so the chat
 * workflow can force the turn to stop after the model calls it (via the
 * AI SDK `hasToolCall` stop condition) and the clients can detect the
 * active prompt without hardcoding the string in three places.
 */
export const MULTIPLE_CHOICE_TOOL_NAME = "multipleChoice";

/**
 * Verbatim user message sent when the user dismisses the picker with
 * "Skip". Sending a real user turn (rather than tracking a per-session
 * "skipped" set on the client) is what collapses the picker back to the
 * standard composer: the `multipleChoice` tool call is no longer the
 * last message, so `getActiveMultipleChoice` returns `null`. It also
 * tells the model the user declined so it can move on.
 */
export const MULTIPLE_CHOICE_SKIP_MESSAGE =
  "I'd rather not pick from those options.";

export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 10;

/**
 * Coerce a tool part's `output` or `input` into a clean
 * {@link SdkMultipleChoiceState}, or `null` when it can't form a usable
 * prompt. Both `output` and `input` carry the same shape (`execute` just
 * echoes the input back), and during streaming / history rehydration
 * either may be a deep-partial with holes — so we require a non-empty
 * `question`, drop non-string/empty options, and normalize the boolean
 * flags. Shared by the web + mobile pickers, their transcript views, and
 * the persist-step finalizer so the coercion rules can't drift apart.
 *
 * Set `requireOptions: false` for surfaces that still want to render with
 * an empty option list (e.g. the read-only transcript view, which shows
 * the question alone while options stream in, and the persist finalizer,
 * which must always materialize a terminal output). The picker keeps the
 * default (`true`) so it reverts to the standard composer until a real,
 * non-empty option set has arrived.
 */
export function coerceMultipleChoiceState(
  raw: unknown,
  { requireOptions = true }: { requireOptions?: boolean } = {}
): SdkMultipleChoiceState | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const candidate = raw as {
    question?: unknown;
    options?: unknown;
    multiple?: unknown;
    allowOther?: unknown;
  };
  if (typeof candidate.question !== "string" || !candidate.question) {
    return null;
  }
  const options = Array.isArray(candidate.options)
    ? candidate.options.filter(
        (o): o is string => typeof o === "string" && o.length > 0
      )
    : [];
  if (requireOptions && options.length === 0) {
    return null;
  }
  return {
    question: candidate.question,
    options,
    multiple: candidate.multiple === true,
    allowOther: candidate.allowOther === true,
  };
}

/**
 * One prompt's worth of picked labels, as collected by a picker.
 */
export interface SdkMultipleChoiceAnswer {
  multiple: boolean;
  /** Chosen option labels (plus any free-text "Other" answer), in order. */
  parts: readonly string[];
  question: string;
}

/**
 * Render the user's picks as the verbatim text of their next message.
 *
 * Multi-select answers become a markdown bullet list — a bare "\n" is a
 * soft break the message renderer collapses to a space. A single prompt
 * sends the bare answer; a batch (the model called `multipleChoice` more
 * than once in one turn) prefixes each answer with its question so the
 * model can map answers back to the calls it made. Answers with no picks
 * are dropped, which is how a partially answered batch reads as "these
 * are the ones I have an opinion on".
 *
 * Shared by the web and mobile pickers so the wire format can't drift.
 */
export function formatMultipleChoiceAnswer(
  answers: readonly SdkMultipleChoiceAnswer[]
): string {
  const label = answers.length > 1;
  return answers
    .filter((answer) => answer.parts.length > 0)
    .map((answer) => {
      const body = answer.multiple
        ? answer.parts.map((part) => `- ${part}`).join("\n")
        : answer.parts.join(", ");
      return label ? `**${answer.question}**\n${body}` : body;
    })
    .join("\n\n");
}
